import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { useAppSelector, useAppDispatch } from './hooks/redux';
import { loadUser } from './store/slices/authSlice';
import ErrorBoundary from './components/UI/ErrorBoundary';
import ToastContainer from './components/UI/Toast';
import WebSocketProvider from './components/Providers/WebSocketProvider';
import WebRTCProvider from './components/Providers/WebRTCProvider';
import ThemeProvider from './components/Providers/ThemeProvider';

// Pages
import HomePage from './pages/HomePage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import VotePage from './pages/VotePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import CreateProjectPage from './pages/CreateProjectPage';
import EditProjectPage from './pages/EditProjectPage';
import ProfilePage from './pages/ProfilePage';
import KYCPage from './pages/KYCPage';
import AboutPage from './pages/AboutPage';
import WalletPage from './pages/WalletPage';

// Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';

function AppContent() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, accessToken } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // Load user if token exists
    if (accessToken) {
      dispatch(loadUser());
    }
  }, [dispatch, accessToken]);

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<AboutPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="vote" element={<VotePage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="projects/create"
          element={
            <ProtectedRoute>
              <CreateProjectPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="projects/:id/edit"
          element={
            <ProtectedRoute>
              <EditProjectPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="kyc"
          element={
            <ProtectedRoute>
              <KYCPage />
            </ProtectedRoute>
          }
        />
        <Route path="wallet" element={<WalletPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ErrorBoundary>
        <ThemeProvider>
          <BrowserRouter>
            <WebSocketProvider>
              <WebRTCProvider>
                <AppContent />
                <ToastContainer />
              </WebRTCProvider>
            </WebSocketProvider>
          </BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
    </Provider>
  );
}

export default App;

